<template>
  <div
    class="batches-filter"
    @keyup.enter="submitSearch"
  >
    <card title="Filtros">
      <div class="row mb-5">
        <search-field-selector
          :limit-date="limitDate"
          :default-start-date="today"
          :default-end-date="today"
          :options="dateSelectOptions"
          @updateSearchValue="setDate"
        />

        <field-container
          v-if="isAllowedCollectionPoint"
          label="Posto de coleta"
        >
          <select-search
            id="txtCollectionPointName"
            ref="refSearchCollectionPoint"
            :value="collectionPoint"
            :options="collectionPointLabels"
            placeholder="Selecione um posto de coleta"
            :uppercase-all-items="true"
            :footer-enabled="false"
            :no-results-message-enabled="true"
            :icon-visible="false"
            no-results-text="Não foi encontrado posto de coleta cadastrado com esse nome"
            @change="setCollectionPoint"
          />
        </field-container>

        <md-button
          class="btn btn-nonenary"
          @click="submitSearch"
        >
          <svg-loader src="/static/images/icon-generate-list.svg" />

          Listar
        </md-button>
      </div>

      <batch-packaging-select
        v-model="packagingSelected"
        class="mb-2"
      />

      <div
        :class="{
          'more-filters-open': isMoreOptionsVisible,
        }"
      >
        <md-button
          class="more-filters-button"
          @click="handleToggleMoreFilters"
        >
          Mais filtros

          <md-icon
            md-src="/static/images/arrow-up-primary-color.svg"
            class="more-filters-button-icon"
          />
        </md-button>

        <transition name="dropdown">
          <div v-show="isMoreOptionsVisible">
            <div>
              <p class="mb-2">Origem da OS:</p>

              <md-layout class="gap-4">
                <md-radio
                  v-for="(option, index) in osOriginOptions"
                  :key="index"
                  v-model="osSource"
                  :md-value="option.value"
                  class="mb-0"
                >
                  {{ option.label }}
                </md-radio>
              </md-layout>
            </div>
          </div>
        </transition>
      </div>
    </card>

    <label-data-title
      label="Listagem de lotes"
      :data="currentFilterLabel"
      class="mt-5"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import Cookies from 'js-cookie'

import { formatDate } from '@/utils/date-utils'
import {
  BATCH_PACKAGING_FROZEN_ID,
  BATCH_PACKAGING_REFRIGERATED_DRY_ID,
  BATCH_PACKAGING_REFRIGERATED_ID,
  BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
} from '@/enums'

import Card from '@/components/common/Card'
import ButtonIcon from '@/components/common/ButtonIcon'
import SearchFieldSelector from '@/components/common/SearchFieldSelector'
import FieldContainer from '@/components/common/FieldContainer'
import SelectSearch from '@/components/common/SelectSearch'
import BatchPackagingSelect from '@/components/common/BatchPackagingSelect'
import LabelDataTitle from '@/components/common/LabelDataTitle'

export default {
  name: 'BatchesFilter',
  components: {
    Card,
    ButtonIcon,
    SearchFieldSelector,
    FieldContainer,
    SelectSearch,
    BatchPackagingSelect,
    LabelDataTitle,
  },
  data() {
    return {
      refSearchCollectionPoint: null,
      today: new Date(),
      dateSelected: null,
      activeFilter: null,
      packagingSelected: [
        BATCH_PACKAGING_FROZEN_ID,
        BATCH_PACKAGING_REFRIGERATED_ID,
        BATCH_PACKAGING_ROOM_TEMPERATURE_ID,
        BATCH_PACKAGING_REFRIGERATED_DRY_ID,
      ],
      collectionPoint: null,
      integratedRoot: '',
      filtersInitialized: false,
      showModalCollectionPointModal: false,
      limitDaysBatches: 10,
      dateSelectOptions: [
        {
          itemValue: 'TD',
          value: 'transferDate',
          label: 'Período',
          placeholder: 'Período',
          limitDays: 1827,
          type: 'date',
        },
      ],
      osSource: '',
      isMoreOptionsVisible: false,
      osOriginOptions: [
        {
          value: '',
          label: 'Todas',
        },
        {
          value: 'AOL2',
          label: 'AOL',
        },
        {
          value: 'AOLWS',
          label: 'LIS',
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['collectionPointLabels', 'toggle', 'batchesAmount']),
    currentFilterLabel() {
      const formattedStartDate = moment(
        this.activeFilter?.dateSelected?.start ?? this.today
      ).format('DD/MM/YYYY')
      const formattedEndDate = moment(
        this.activeFilter?.dateSelected?.end ?? this.today
      ).format('DD/MM/YYYY')

      let formattedDate =
        formattedStartDate === formattedEndDate
          ? formattedStartDate
          : `${formattedStartDate} até ${formattedEndDate}`

      if (formattedDate === moment(this.today).format('DD/MM/YYYY')) {
        formattedDate = 'hoje'
      }

      return `de ${formattedDate} (${this.batchesAmount})`
    },
    isAllowedCollectionPoint() {
      const collectPlace = this.toggle?.collectPlace
      return collectPlace && this.collectionPointLabels?.length > 0
    },
    startDate() {
      return formatDate(this.dateSelected?.start, 4)
    },
    endDate() {
      return formatDate(this.dateSelected?.end, 4)
    },
    limitDate() {
      return moment(this.today, 'DD/MM/YYYY')
        .subtract(this.limitDaysBatches, 'days')
        .toDate()
    },
    batchFilter() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        packagingType: this.packagingSelected,
        integrated: this.integratedRoot,
        collectionPointName: this.collectionPoint?.label,
        source: this.osSource,
      }
    },
  },
  watch: {
    '$route.query.reload': {
      handler(value) {
        if (value) {
          this.submitSearch()

          this.$router.replace({
            query: {
              ...this.$route.query,
              reload: undefined,
            },
          })
        }
      },
    },
    packagingSelected() {
      if (!this.filtersInitialized) return
      this.submitSearch()
    },
  },
  async mounted() {
    this.setInitialFilters()
    await this.getAllCollectionPointList()
    this.getSelectedCollectionPoint()
    this.$nextTick(async () => {
      this.submitSearch()
      this.filtersInitialized = true
    })
  },
  methods: {
    ...mapActions(['selectBatchList', 'batchGetBatches', 'getAllCollectionPointList']),
    setInitialFilters() {
      this.dateSelected = {
        start: this.today,
        end: this.today,
      }
    },
    getSelectedCollectionPoint() {
      if (!this.isAllowedCollectionPoint) return

      const savedCollectionPoint = Cookies.get('selectedCollectionPoint')

      if (savedCollectionPoint) {
        this.$nextTick(() => {
          this.$refs.refSearchCollectionPoint.field = JSON.parse(savedCollectionPoint)
          this.collectionPoint = JSON.parse(savedCollectionPoint)
        })
      } else if (this.isAllowedCollectionPoint) {
        this.showModalCollectionPointModal = true
      }
    },
    setDate(e) {
      const { date } = e
      this.dateSelected = date
    },
    setCollectionPoint(event) {
      if (event) {
        Cookies.set('selectedCollectionPoint', JSON.stringify(event), { expires: 7 })
        this.collectionPoint = event
        this.$refs.refSearchCollectionPoint.field = this.collectionPoint
      } else {
        Cookies.remove('selectedCollectionPoint')
        this.collectionPoint = null
      }
    },
    setCollectionPointModal(event) {
      if (event) {
        this.$nextTick(() => {
          this.setCollectionPoint(event)
          this.submitSearch()
        })
      } else {
        Cookies.remove('selectedCollectionPoint')
        this.collectionPoint = null
      }
    },
    async submitSearch() {
      this.selectBatchList(this.batchFilter)
      await this.batchGetBatches()
      this.activeFilter = {
        dateSelected: this.dateSelected,
        collectionPoint: this.collectionPoint,
      }
    },
    hintPackagingType(value) {
      const packagingSelected = this.packagingSelected
      const hintSelect = 'Selecione para filtrar uma ou mais formas de acondicionamento'
      const hintDeselect = 'Clique para remover filtro'

      if (packagingSelected?.length === 4 && value) return hintSelect

      if (value) return hintDeselect

      return hintSelect
    },
    handleToggleMoreFilters() {
      this.isMoreOptionsVisible = !this.isMoreOptionsVisible
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

:deep(.search .search-field-selector) {
  height: 40px;
  min-height: auto;
}

:deep(.field) {
  height: 40px !important;
}

.batches-filter {
  .btn {
    height: 40px;
    display: flex;
    justify-items: center;
    gap: 0.5rem;

    .md-icon {
      width: 1.375rem;
      height: 1.375rem;
      min-width: auto;
      min-height: auto;
    }
  }

  .md-radio {
    margin: 0;
  }

  .more-filters {
    &-button {
      margin: 0;
      padding: 0;
      background-color: transparent;
      color: $theme-primary-color;
      min-height: auto;
      height: auto;

      &:hover {
        background-color: transparent;
      }

      &-icon {
        transform: rotate(180deg);
        transition: transform 0.3s ease-in-out;
      }

      .md-icon {
        min-width: auto;
        min-height: auto;
        width: 1rem;
        height: 1rem;
      }

      ::v-deep(.md-ink-ripple) {
        display: none !important;
      }
    }
  }

  .more-filters-open .more-filters-button-icon {
    transform: rotate(0deg) !important;
  }
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;

  div {
    flex: 1 1 auto;
    margin: 0;
  }
}

:deep(.md-theme-default.md-radio .md-radio-container:after) {
  background-color: $theme-primary-color !important;
}

:deep(.md-theme-default.md-radio.md-checked .md-radio-container) {
  border-color: $theme-primary-color !important;
}

::v-deep(.card-form__container .title-main) {
  color: $theme-primary-color;
}
</style>
